// @flow

import React, {createContext, useCallback, useMemo, useRef, useState} from 'react';
import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';
import {SORTABLE_POSITION} from '@pb/kit-component-native-sortable';

import type {Node} from 'react';

import type {TargetElementType} from '../typedef';


type Listener = (e: SyntheticDragEvent<HTMLElement>) => void;

export type DataTableDragEventsContextType = {|
    onDrop: (e: SyntheticDragEvent<HTMLElement>) => void,
    onDragOver: (e: SyntheticDragEvent<HTMLElement>) => void,
    onDragEnter: (e: SyntheticDragEvent<HTMLElement>) => void,
    onDragStart: (e: SyntheticDragEvent<HTMLElement>) => void,
    onDragEnd: (e: SyntheticDragEvent<HTMLElement>) => void,
    setOnDropSubscriber: (callback: (e: SyntheticDragEvent<HTMLElement>, target: ?TargetElementType) => void) => void,
    setOnDragSubscriber: (callback: (e: SyntheticDragEvent<HTMLElement>, target: TargetElementType) => void) => void,
    resetOnDropSubscriber: () => void,
    resetOnDragSubscriber: () => void,
    targetElement: ?TargetElementType,
    addListener: (type: string, listener: Listener) => void,
|};

export const DRAG_EVENTS_DEFAULT_CONTEXT: DataTableDragEventsContextType = {
    onDragEnter: () => undefined,
    onDragOver: () => undefined,
    onDragStart: () => undefined,
    onDragEnd: () => undefined,
    onDrop: () => undefined,
    setOnDropSubscriber: () => undefined,
    resetOnDropSubscriber: () => undefined,
    setOnDragSubscriber: () => undefined,
    resetOnDragSubscriber: () => undefined,
    addListener: () => undefined,
    targetElement: null
};

const dataIdAttr = 'id';
export const ALLOWED_DRAG_AND_DROP_AREA_DATA_ID = 'allowed-drag&drop-area';

export const DataTableDragEventsContext: React$Context<DataTableDragEventsContextType> =
    createContext<DataTableDragEventsContextType>(DRAG_EVENTS_DEFAULT_CONTEXT);
DataTableDragEventsContext.displayName = 'DataTableDragEventsContext';

type Props = {|
    children: Node,
|};

export function DataTableDragEventsContextProvider({children}: Props): Node {
    const [targetElement, setTargetElement] = useState<TargetElementType | null>(null);
    const onDropSubscriber = useRef(null);
    const onDragSubscriber = useRef(null);
    const listeners = useRef<{ [string]: Listener[] }>(new Map());
    const addListener: $PropertyType<DataTableDragEventsContextType, 'addListener'> = useCallbackWithRef((type, listener) => {
        if (!listeners.current[type]) {
            listeners.current[type] = [];
        }
        listeners.current[type].push(listener);
    });


    const onDrop = useCallbackWithRef((e: SyntheticDragEvent<HTMLElement>) => {
        e.preventDefault();

        if (onDropSubscriber.current) {
            onDropSubscriber.current(e, targetElement);
        }
    });

    const onDragOver = useCallbackWithRef((e: SyntheticDragEvent<HTMLElement>) => {
        e.preventDefault();

        const target = ((e.target: any): HTMLElement);
        const definingElement: HTMLElement = (target.closest(`[data-${dataIdAttr}]`): any);

        if (definingElement) {
            const {width, left} = definingElement.getBoundingClientRect();
            const dataId = definingElement.dataset[dataIdAttr];
            const sortablePosition = e.pageX > (left + width / 2) ? SORTABLE_POSITION.AFTER : SORTABLE_POSITION.BEFORE;

            if (targetElement?.dataId !== dataId || targetElement?.sortablePosition !== sortablePosition) {
                setTargetElement({dataId, sortablePosition});

                if (onDragSubscriber.current) {
                    onDragSubscriber.current(e, {dataId, sortablePosition});
                }
            }
        }
    });

    const onDragEnter = useCallback((event: SyntheticDragEvent<HTMLElement>) => {
        event.preventDefault();
    }, []);

    const setOnDropSubscriber = useCallbackWithRef(callback => {
        onDropSubscriber.current = callback;
    });

    const setOnDragSubscriber = useCallbackWithRef(callback => {
        onDragSubscriber.current = callback;
    });

    const resetOnDropSubscriber = useCallbackWithRef(() => {
        onDropSubscriber.current = null;
    });

    const resetOnDragSubscriber = useCallbackWithRef(() => {
        onDragSubscriber.current = null;
    });

    const onDragStart = useCallbackWithRef((e: SyntheticDragEvent<HTMLElement>) => {
        // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
        listeners.current.dragStart?.forEach(listener => {
            listener(e);
        });
    });

    const onDragEnd = useCallbackWithRef((e: SyntheticDragEvent<HTMLElement>) => {
        // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
        listeners.current.dragEnd?.forEach(listener => {
            listener(e);
        });
    });

    const value = useMemo(() => ({
        onDrop,
        onDragOver,
        onDragEnter,
        onDragStart,
        onDragEnd,
        addListener,
        setOnDropSubscriber,
        setOnDragSubscriber,
        resetOnDropSubscriber,
        resetOnDragSubscriber,
        targetElement
    }), [
        onDrop,
        onDragOver,
        onDragEnter,
        onDragStart,
        onDragEnd,
        addListener,
        setOnDropSubscriber,
        setOnDragSubscriber,
        resetOnDropSubscriber,
        resetOnDragSubscriber,
        targetElement,
    ]);

    return (
        <DataTableDragEventsContext.Provider value={value}>
            {children}
        </DataTableDragEventsContext.Provider>
    );
}

            // @pb/generate-flow-typed Webpack Loader:
            // Duplicates all internal dependencies imports
            // to be sure all deps are parsed by this loader
            // cause Babel loader removes `import type ...`
            // and e.g. 'typedef.js' files are not included in dist
            
require("../typedef");
